// A customer's recordings, managed by the operator.
//
// Kevin, 2026-08-27: "I have all the recordings so maybe having a separate tab or page that the
// operator (me) can manage recordings would be useful to allow me to upload all the recordings."
//
// WHY A TAB AND NOT A PAGE. His call, and it matches where the work happens: a recording belongs to
// a customer, and every use of one — a menu greeting, a voicemail greeting — is reached from that
// customer's lines. A top-level library would have to answer "whose is this?" in a column, which is
// the question the tab answers by existing.
//
// WHY MULTI-FILE. The setup case is somebody with a folder of prompts already recorded, not somebody
// composing one. Uploading them one modal at a time was the whole complaint. So the drop zone takes
// as many files as you give it, converts each in the browser, and reports per file — because a
// batch that fails halfway must say WHICH one, and leave the rest saved.
//
// THE NAME COMES FROM THE FILENAME, minus its extension, because that is what somebody who recorded
// twelve prompts has already encoded. It stays editable per row afterwards.
//
// NO TOKEN IN AN <audio src>. Playback fetches with the Authorization header and plays a blob URL.
// A signed URL would put a session JWT in browser history and in every proxy log between here and
// the customer's screenshot.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { encodeWavFromBlob } from '../../lib/wavEncode.js';
import ActionMenu from '../shared/ActionMenu.jsx';
import FormModal from '../shared/FormModal.jsx';
import AddButton from '../shared/AddButton.jsx';
import ConfirmDeleteDialog from '../shared/ConfirmDeleteDialog.jsx';

/** Words for the two kinds. The stored values are `greeting` and `ivr_prompt`. */
const KIND_LABEL = {
  greeting: 'Voicemail greeting',
  ivr_prompt: 'Menu prompt',
};

const nameFromFile = (f) => String(f?.name || '').replace(/\.[^.]+$/, '').slice(0, 60) || 'Recording';

const secs = (n) => (n == null ? '—' : `${Math.round(n)}s`);
const kb = (n) => (n == null ? '—' : `${Math.max(1, Math.round(n / 1024))} KB`);

function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Upload several files, reporting each.
 *
 * Sequential rather than parallel on purpose: each file is decoded and resampled in the browser,
 * and four 8 kHz conversions at once on a laptop makes the tab unresponsive for long enough that
 * an operator presses the button again.
 */
function UploadModal({ subscriberId, onClose, canWrite }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [kind, setKind] = useState('ivr_prompt');
  const [rows, setRows] = useState([]); // { file, name, status, error? }

  const take = (fileList) => {
    const incoming = [...(fileList || [])].filter((f) => f && f.size > 0);
    if (!incoming.length) return;
    setRows((prev) => [
      ...prev,
      ...incoming.map((file) => ({ file, name: nameFromFile(file), status: 'ready' })),
    ]);
  };

  const run = useMutation({
    mutationFn: async () => {
      const results = [];
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (row.status === 'done') { results.push(row); continue; }
        setRows((prev) => prev.map((r, n) => (n === i ? { ...r, status: 'working' } : r)));
        try {
          const wav = await encodeWavFromBlob(row.file);
          const form = new FormData();
          form.append('audio', new Blob([wav], { type: 'audio/wav' }), 'recording.wav');
          form.append('name', row.name.trim() || nameFromFile(row.file));
          form.append('kind', kind);
          await api.post(`/subscribers/${subscriberId}/recordings`, form);
          setRows((prev) => prev.map((r, n) => (n === i ? { ...r, status: 'done' } : r)));
          results.push({ ...row, status: 'done' });
        } catch (err) {
          const message = err?.body?.error || err?.message || 'Could not save this one.';
          setRows((prev) => prev.map((r, n) => (n === i ? { ...r, status: 'failed', error: message } : r)));
          results.push({ ...row, status: 'failed', error: message });
        }
        qc.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'recordings'] });
      }
      return results;
    },
  });

  const done = rows.filter((r) => r.status === 'done').length;
  const failed = rows.filter((r) => r.status === 'failed');
  const pending = rows.some((r) => r.status === 'ready');
  const finished = run.isSuccess && !pending;

  return (
    <FormModal
      title="Add recordings"
      subtitle="Drop in as many as you like. They are saved to this customer's library — nothing is put on a phone line until you choose it as a greeting or a menu prompt."
      onClose={onClose}
      size="lg"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {rows.length ? `${done} of ${rows.length} saved` : 'Nothing chosen yet'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {finished ? 'Close' : 'Cancel'}
            </button>
            <button
              type="button"
              disabled={!canWrite || run.isPending || !pending}
              onClick={() => run.mutate()}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {run.isPending ? 'Saving…' : `Save ${rows.filter((r) => r.status !== 'done').length || ''}`.trim()}
            </button>
          </div>
        </div>
      )}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="rec-kind" className="block text-sm font-medium text-gray-900">
            What are these for?
          </label>
          <select
            id="rec-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            disabled={run.isPending}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
          >
            <option value="ivr_prompt">Menu prompts — what callers hear from an auto attendant</option>
            <option value="greeting">Voicemail greetings</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Applies to everything in this batch. Add a second batch for the other kind.
          </p>
        </div>

        {/* Click, tab-and-Enter, or drop. All three, because the first version had only the
            invisible third one and Kevin could not find it. */}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); take(e.dataTransfer?.files); }}
          className={`flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-8 text-sm transition-colors ${
            dragging ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400'
          }`}
        >
          <span className="font-medium text-gray-900">Choose files, or drag them here</span>
          <span className="text-xs text-gray-500">Any common audio format. We convert them for the phone system.</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          multiple
          className="hidden"
          aria-label="Audio files"
          onChange={(e) => { take(e.target.files); e.target.value = ''; }}
        />

        {rows.length > 0 && (
          <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
            {rows.map((r, i) => (
              <li key={`${r.file.name}-${i}`} className="flex items-center gap-3 px-3 py-2">
                <input
                  value={r.name}
                  onChange={(e) => setRows((prev) => prev.map((x, n) => (n === i ? { ...x, name: e.target.value } : x)))}
                  disabled={run.isPending || r.status === 'done'}
                  aria-label={`Name for ${r.file.name}`}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 disabled:bg-gray-50 disabled:text-gray-500"
                />
                <span className="shrink-0 text-xs">
                  {r.status === 'done' && <span className="text-green-700">Saved</span>}
                  {r.status === 'working' && <span className="text-gray-500">Saving…</span>}
                  {r.status === 'ready' && <span className="text-gray-400">Ready</span>}
                  {r.status === 'failed' && <span className="text-red-700">{r.error}</span>}
                </span>
                {r.status === 'ready' && !run.isPending && (
                  <button
                    type="button"
                    onClick={() => setRows((prev) => prev.filter((_, n) => n !== i))}
                    className="shrink-0 text-xs text-gray-500 hover:text-gray-800"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* A PARTIAL BATCH IS NOT A FAILURE AND NOT A SUCCESS. Saying which ones landed is the
            difference between re-adding one file and re-adding twelve. */}
        {failed.length > 0 && !run.isPending && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {done > 0
              ? `${done} saved, ${failed.length} did not. The ones that failed are still listed above — fix the name or the file and press Save again.`
              : 'None of these could be saved. The reason is beside each one.'}
          </div>
        )}
      </div>
    </FormModal>
  );
}

function RenameModal({ subscriberId, item, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState(item.name || '');
  const save = useMutation({
    mutationFn: () => api.patch(`/subscribers/${subscriberId}/recordings/${item.id}`, { name: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'recordings'] });
      onClose();
    },
  });

  return (
    <FormModal
      title="Rename recording"
      subtitle="This is the name you see here. It does not change the audio or where it is already being used."
      onClose={onClose}
      size="sm"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    >
      <label htmlFor="rename-rec" className="block text-sm font-medium text-gray-900">Name</label>
      <input
        id="rename-rec"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={60}
        className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
      />
      {save.isError && (
        <p className="mt-2 text-sm text-red-700">{save.error?.body?.error || save.error?.message}</p>
      )}
    </FormModal>
  );
}

/** One row's play button. Holds its own blob URL so it can be revoked. */
function PlayButton({ subscriberId, item }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  if (!item.playable) {
    return <span className="text-xs text-gray-400" title="The audio for this row is missing">No audio</span>;
  }

  if (url) {
    return <audio ref={audioRef} src={url} controls autoPlay className="h-8 w-44" />;
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={loading}
        onClick={async () => {
          setLoading(true); setError(null);
          try {
            // The Authorization header goes on the FETCH, so the token never appears in a URL.
            const blob = await api.getBlob(`/subscribers/${subscriberId}/recordings/${item.id}/audio`);
            setUrl(URL.createObjectURL(blob));
          } catch (err) {
            setError(err?.body?.error || err?.message || 'Could not play it.');
          } finally {
            setLoading(false);
          }
        }}
        className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {loading ? '…' : 'Play'}
      </button>
      {error && <span className="text-xs text-red-700">{error}</span>}
    </div>
  );
}

export default function RecordingsTab({ subscriberId, canWrite = false }) {
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'recordings'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/recordings`),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/subscribers/${subscriberId}/recordings/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriber', String(subscriberId), 'recordings'] });
      setDeleting(null); setDeleteError(null);
    },
    // THE SERVER REFUSES WHILE A MENU PROMPT POINTS AT IT, and names the slot. Surfacing that
    // beats a generic failure: the operator's next move is to change the slot, not to retry.
    onError: (err) => setDeleteError(err?.body?.error || err?.message || 'Could not delete it.'),
  });

  const items = data?.items || [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Recordings</h2>
          <p className="mt-1 text-sm text-gray-600">
            Audio for this customer — greetings and the prompts callers hear from a menu.
          </p>
        </div>
        {canWrite && <AddButton onClick={() => setAdding(true)}>Add recordings</AddButton>}
      </div>

      {isLoading && <p className="mt-4 text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p className="mt-4 text-sm text-red-700">{error?.body?.error || error?.message}</p>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <p className="mt-6 text-center text-sm text-gray-500">
          No recordings yet.{canWrite ? ' Add the ones you already have — you can name them as you go.' : ''}
        </p>
      )}

      {items.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-2 py-2 font-medium">Name</th>
                <th className="px-2 py-2 font-medium">Used for</th>
                <th className="px-2 py-2 font-medium">Length</th>
                <th className="px-2 py-2 font-medium">Size</th>
                <th className="px-2 py-2 font-medium">Added</th>
                <th className="px-2 py-2 font-medium">Listen</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((r) => (
                <tr key={r.id} className="align-middle">
                  <td className="px-2 py-2">
                    <span className="font-medium text-gray-900">{r.name}</span>
                    {r.transcript && (
                      <span className="mt-0.5 block max-w-md truncate text-xs text-gray-500" title={r.transcript}>
                        “{r.transcript}”
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-gray-700">{KIND_LABEL[r.kind] || r.kind}</td>
                  <td className="px-2 py-2 text-gray-700">{secs(r.duration_sec)}</td>
                  <td className="px-2 py-2 text-gray-700">{kb(r.bytes)}</td>
                  <td className="px-2 py-2 text-gray-700">{when(r.created_at)}</td>
                  <td className="px-2 py-2">
                    <PlayButton subscriberId={subscriberId} item={r} />
                  </td>
                  <td className="px-2 py-2 text-right">
                    {canWrite && (
                      <ActionMenu
                        title={`Actions for ${r.name}`}
                        items={[
                          { label: 'Rename', onClick: () => setRenaming(r) },
                          { divider: true },
                          { label: 'Delete', danger: true, onClick: () => { setDeleteError(null); setDeleting(r); } },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <UploadModal subscriberId={subscriberId} canWrite={canWrite} onClose={() => setAdding(false)} />
      )}
      {renaming && (
        <RenameModal subscriberId={subscriberId} item={renaming} onClose={() => setRenaming(null)} />
      )}
      {deleting && (
        <ConfirmDeleteDialog
          title={`Delete “${deleting.name}”?`}
          body="The audio is removed from this customer's library. Anything already using it keeps playing what is on the phone system until you change that slot."
          confirmLabel="Delete"
          // NOT type-the-word destructive. That ceremony is for a customer or a line; a recording
          // is one file in a library, and the server refuses outright while a menu points at it —
          // which is the case actually worth stopping.
          destructive={false}
          isPending={remove.isPending}
          error={deleteError}
          onCancel={() => { setDeleting(null); setDeleteError(null); }}
          onConfirm={() => remove.mutate(deleting.id)}
        />
      )}
    </div>
  );
}
